import { useState } from "react";
import { useForm } from "react-hook-form";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, MODE_LABELS, type LocatRight, type MarketRef } from "../../api/client";
import { queryKeys } from "../../api/queryKeys";
import { useTheme } from "../../contexts/ThemeContext";
import { CenteredMessage } from "../CenteredMessage";
import { Chip } from "../Chip";
import { SelectField, TextField } from "../form";

const MODE_OPTIONS = Object.entries(MODE_LABELS).map(([value, label]) => ({ value, label }));

/** Rattachement d'un marché à un droit locat (numExpiry : 0 = toutes les échéances). */
function MarketAddForm({ grant, markets, idUserAPI }: { grant: LocatRight; markets: MarketRef[]; idUserAPI: number }) {
  const { t } = useTheme();
  const queryClient = useQueryClient();
  const { control, handleSubmit, reset } = useForm<{ idMarkets: string; numExpiry: string }>({
    defaultValues: { idMarkets: "", numExpiry: "0" },
  });

  const addMut = useMutation({
    mutationFn: (v: { idMarkets: string; numExpiry: string }) =>
      api.rights.addMarket(grant.idUserAPILocat, { idMarkets: Number(v.idMarkets), numExpiry: Number(v.numExpiry) }),
    onSuccess: () => reset(),
    onSettled: () => queryClient.invalidateQueries({ queryKey: queryKeys.rights(idUserAPI) }),
  });

  const candidates = markets.filter(m => m.locat === grant.locat);
  const onSubmit = handleSubmit(v => addMut.mutate(v));

  return (
    <form onSubmit={onSubmit} style={{ display: "flex", gap: 8, alignItems: "flex-end", marginTop: 8 }}>
      <div style={{ flex: 1, maxWidth: 320 }}>
        <SelectField
          control={control}
          name="idMarkets"
          placeholder="Ajouter un marché…"
          options={candidates.map(m => ({ value: String(m.idMarkets), label: m.labelledMarkets }))}
          rules={{ required: "Marché obligatoire" }}
        />
      </div>
      <div style={{ width: 110 }} title="0 = toutes les échéances, N = échéance précise">
        <TextField
          control={control}
          name="numExpiry"
          placeholder="Échéance"
          rules={{ required: "Requis", pattern: { value: /^\d+$/, message: "Entier" } }}
        />
      </div>
      <button type="submit" disabled={addMut.isPending} style={{
        border: `1px solid ${t.rowBorder}`, background: t.panelBg, color: t.accent,
        borderRadius: 8, padding: "10px 13px", fontSize: 12.5, fontWeight: 600,
        fontFamily: "inherit", cursor: addMut.isPending ? "not-allowed" : "pointer", whiteSpace: "nowrap",
      }}>+ Marché</button>
      {addMut.isError && (
        <span style={{ fontSize: 12, color: t.lateColor }}>{(addMut.error as Error).message}</span>
      )}
    </form>
  );
}

/** Rattachement d'un marché options à un droit locat. */
function OptionAddForm({ grant, markets, idUserAPI }: { grant: LocatRight; markets: MarketRef[]; idUserAPI: number }) {
  const { t } = useTheme();
  const queryClient = useQueryClient();
  const { control, handleSubmit, reset } = useForm<{ idMarket: string }>({ defaultValues: { idMarket: "" } });

  const addMut = useMutation({
    mutationFn: (v: { idMarket: string }) =>
      api.rights.addOption(grant.idUserAPILocat, { idMarket: Number(v.idMarket) }),
    onSuccess: () => reset(),
    onSettled: () => queryClient.invalidateQueries({ queryKey: queryKeys.rights(idUserAPI) }),
  });

  const candidates = markets.filter(m => m.locat === grant.locat);
  const onSubmit = handleSubmit(v => addMut.mutate(v));

  return (
    <form onSubmit={onSubmit} style={{ display: "flex", gap: 8, alignItems: "flex-end", marginTop: 8 }}>
      <div style={{ flex: 1, maxWidth: 320 }}>
        <SelectField
          control={control}
          name="idMarket"
          placeholder="Ajouter un marché options…"
          options={candidates.map(m => ({ value: String(m.idMarkets), label: m.labelledMarkets }))}
          rules={{ required: "Marché obligatoire" }}
        />
      </div>
      <button type="submit" disabled={addMut.isPending} style={{
        border: `1px solid ${t.rowBorder}`, background: t.panelBg, color: t.accent,
        borderRadius: 8, padding: "10px 13px", fontSize: 12.5, fontWeight: 600,
        fontFamily: "inherit", cursor: addMut.isPending ? "not-allowed" : "pointer", whiteSpace: "nowrap",
      }}>+ Option</button>
      {addMut.isError && (
        <span style={{ fontSize: 12, color: t.lateColor }}>{(addMut.error as Error).message}</span>
      )}
    </form>
  );
}

/**
 * Onglet "Droits marchés" : les droits locat (place + mode) du compte, avec leurs
 * marchés et marchés options rattachés. Un droit se déplie pour gérer ses rattachements.
 */
export function MarketsTab({ idUserAPI }: { idUserAPI: number }) {
  const { t } = useTheme();
  const queryClient = useQueryClient();
  const [removeError, setRemoveError] = useState<string | null>(null);
  // State d'édition : seulement l'identifiant du droit déplié.
  const [expandedId, setExpandedId] = useState<number | null>(null);

  const rightsQ = useQuery({ queryKey: queryKeys.rights(idUserAPI), queryFn: () => api.rights.get(idUserAPI) });
  const locatsRefQ = useQuery({ queryKey: queryKeys.locatsReferential, queryFn: api.rights.locatsReferential });
  const marketsRefQ = useQuery({ queryKey: queryKeys.marketsReferential, queryFn: api.rights.marketsReferential });

  // Ajout future : place + mode + carnet d'ordres.
  const futureForm = useForm<{ locat: string; mode: string; interdireCarnetOrdre: boolean }>({
    defaultValues: { locat: "", mode: "", interdireCarnetOrdre: false },
  });
  // Ajout cash : place seule (le cash n'a pas de tier ni de carnet d'ordres).
  const cashForm = useForm<{ locat: string }>({ defaultValues: { locat: "" } });

  const invalidateRights = () => queryClient.invalidateQueries({ queryKey: queryKeys.rights(idUserAPI) });

  const addFutureMut = useMutation({
    mutationFn: (v: { locat: string; mode: string; interdireCarnetOrdre: boolean }) =>
      api.rights.addLocat(idUserAPI, { locat: Number(v.locat), mode: Number(v.mode), interdireCarnetOrdre: v.interdireCarnetOrdre }),
    onSuccess: () => futureForm.reset(),
    onSettled: invalidateRights,
  });

  // Cash : stocké en mode Temps réel (3), sans carnet d'ordres (le tier n'a pas de sens pour le cash).
  const addCashMut = useMutation({
    mutationFn: (v: { locat: string }) =>
      api.rights.addLocat(idUserAPI, { locat: Number(v.locat), mode: 3, interdireCarnetOrdre: false }),
    onSuccess: () => cashForm.reset(),
    onSettled: invalidateRights,
  });

  const removeLocatMut = useMutation({
    mutationFn: (id: number) => api.rights.removeLocat(id),
    onMutate: () => setRemoveError(null),
    onError: (e: unknown) => setRemoveError(e instanceof Error ? e.message : "Erreur inconnue"),
    onSettled: invalidateRights,
  });

  const removeMarketMut = useMutation({
    mutationFn: (a: { grantId: number; idMarkets: number }) => api.rights.removeMarket(a.grantId, a.idMarkets),
    onMutate: () => setRemoveError(null),
    onError: (e: unknown) => setRemoveError(e instanceof Error ? e.message : "Erreur inconnue"),
    onSettled: invalidateRights,
  });

  const removeOptionMut = useMutation({
    mutationFn: (a: { grantId: number; idMarket: number }) => api.rights.removeOption(a.grantId, a.idMarket),
    onMutate: () => setRemoveError(null),
    onError: (e: unknown) => setRemoveError(e instanceof Error ? e.message : "Erreur inconnue"),
    onSettled: invalidateRights,
  });

  const onSubmitFuture = futureForm.handleSubmit(v => addFutureMut.mutate(v));
  const onSubmitCash = cashForm.handleSubmit(v => addCashMut.mutate(v));

  const errorMsg = (e: unknown) => (e instanceof Error ? e.message : "Erreur inconnue");
  const addFutureError = addFutureMut.error ? errorMsg(addFutureMut.error) : null;
  const addCashError = addCashMut.error ? errorMsg(addCashMut.error) : null;

  const expiriesLabel = (nums: number[]) =>
    nums.length === 1 && nums[0] === 0 ? "" : ` (éch. ${nums.map(n => (n === 0 ? "toutes" : n)).join(", ")})`;

  if (rightsQ.isLoading || locatsRefQ.isLoading || marketsRefQ.isLoading)
    return <CenteredMessage>Chargement des droits…</CenteredMessage>;
  const failed = [rightsQ, locatsRefQ, marketsRefQ].find(q => q.isError);
  if (failed)
    return <CenteredMessage tone="error">Impossible de charger les droits : {(failed.error as Error).message}</CenteredMessage>;

  const rights = rightsQ.data!;
  const locatsRef = locatsRefQ.data!;
  const marketsRef = marketsRefQ.data!;

  // Une place ne peut être accordée qu'une fois (quel que soit le mode) : on retire du
  // sélecteur celles déjà attribuées au compte. Le backend refuse le doublon de toute façon.
  const grantedLocats = new Set(rights.locats.map(l => l.locat));
  const availableLocats = locatsRef.filter(l => !grantedLocats.has(l.locat));
  const availableFutureLocats = availableLocats.filter(l => l.type !== "C");
  const availableCashLocats = availableLocats.filter(l => l.type === "C");
  const futureRights = rights.locats.filter(l => l.type !== "C");
  const cashRights = rights.locats.filter(l => l.type === "C");

  // Place cash (Locat.type = "C") : gérée au niveau place, sans marché ni option → pas de dépliage.
  const renderLocat = (l: LocatRight) => {
    const isCash = l.type === "C";
    const expanded = !isCash && expandedId === l.idUserAPILocat;
    return (
          <div key={l.idUserAPILocat} style={{
            padding: "10px 12px", borderRadius: 10, background: t.panelBg,
            border: `1px solid ${expanded ? t.accent : t.rowBorder}`, marginBottom: 8,
          }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              {isCash ? (
                <span style={{ width: 18 }} />
              ) : (
                <button
                  onClick={() => setExpandedId(expanded ? null : l.idUserAPILocat)}
                  title={expanded ? "Replier" : "Gérer les marchés et options"}
                  style={{
                    border: "none", background: "transparent", color: t.text2, fontFamily: "inherit",
                    cursor: "pointer", fontSize: 13, padding: 0, width: 18,
                  }}
                >{expanded ? "▾" : "▸"}</button>
              )}
              <div style={{ flex: 1, fontSize: 13.5, fontWeight: 600, color: t.text1 }}>
                {l.nomLocat}
                <span style={{
                  marginLeft: 8, padding: "2px 9px", borderRadius: 20, fontSize: 11.5, whiteSpace: "nowrap",
                  background: t.tagBg, color: t.tagColor, fontWeight: 600,
                }}>{MODE_LABELS[l.mode] ?? `Mode ${l.mode}`}</span>
                <span style={{
                  marginLeft: 6, padding: "1px 8px", borderRadius: 20, fontSize: 11, whiteSpace: "nowrap",
                  border: `1px solid ${t.rowBorder}`, color: t.text2, fontWeight: 600,
                }}>{isCash ? "Cash" : "Future"}</span>
                {l.interdireCarnetOrdre && (
                  <span style={{
                    marginLeft: 6, padding: "2px 9px", borderRadius: 20, fontSize: 11.5, whiteSpace: "nowrap",
                    background: t.lateBg, color: t.lateColor, fontWeight: 600,
                  }}>Carnet d'ordres interdit</span>
                )}
                {!isCash && (
                  <span style={{ marginLeft: 8, fontSize: 12, color: t.text3, fontWeight: 400 }}>
                    {l.markets.length} marché(s), {l.options.length} option(s)
                  </span>
                )}
              </div>
              <button
                onClick={() => removeLocatMut.mutate(l.idUserAPILocat)}
                disabled={removeLocatMut.isPending}
                title="Retirer ce droit"
                style={{
                  border: `1px solid ${t.rowBorder}`, background: t.cardBg, color: t.lateColor,
                  borderRadius: 8, padding: "5px 10px", fontSize: 12, fontWeight: 600,
                  cursor: removeLocatMut.isPending ? "not-allowed" : "pointer", fontFamily: "inherit",
                }}
              >Retirer</button>
            </div>

            {expanded && (
              <div style={{ marginTop: 8, paddingLeft: 28 }}>
                <div style={{ fontSize: 12, color: t.text2, fontWeight: 600 }}>Marchés</div>
                <div>
                  {l.markets.length === 0 && <span style={{ fontSize: 12, color: t.text3 }}>Aucun</span>}
                  {l.markets.map(m => (
                    <Chip
                      key={m.idMarkets}
                      label={m.labelledMarkets + expiriesLabel(m.numExpiries)}
                      removing={removeMarketMut.isPending}
                      onRemove={() => removeMarketMut.mutate({ grantId: l.idUserAPILocat, idMarkets: m.idMarkets })}
                    />
                  ))}
                </div>
                <MarketAddForm grant={l} markets={marketsRef} idUserAPI={idUserAPI} />

                <div style={{ fontSize: 12, color: t.text2, fontWeight: 600, marginTop: 12 }}>Options</div>
                <div>
                  {l.options.length === 0 && <span style={{ fontSize: 12, color: t.text3 }}>Aucune</span>}
                  {l.options.map(o => (
                    <Chip
                      key={o.idMarket}
                      label={o.labelledMarkets}
                      removing={removeOptionMut.isPending}
                      onRemove={() => removeOptionMut.mutate({ grantId: l.idUserAPILocat, idMarket: o.idMarket })}
                    />
                  ))}
                </div>
                <OptionAddForm grant={l} markets={marketsRef} idUserAPI={idUserAPI} />
              </div>
            )}
          </div>
        );
  };

  return (
    <div>
      {removeError && (
        <div style={{ padding: "10px 12px", background: t.lateBg, color: t.lateColor, borderRadius: 8, fontSize: 13, marginBottom: 12 }}>
          {removeError}
        </div>
      )}

      <div style={{ fontSize: 14, fontWeight: 700, color: t.text1, margin: "4px 0 10px" }}>Future</div>
      {futureRights.length === 0 && (
        <div style={{ fontSize: 13, color: t.text2, padding: "0 0 10px" }}>Aucune place future.</div>
      )}
      {futureRights.map(renderLocat)}

      <form onSubmit={onSubmitFuture} style={{
        display: "flex", gap: 10, alignItems: "flex-end", paddingTop: 12, marginTop: 4,
      }}>
        <div style={{ flex: 1, maxWidth: 320 }}>
          <SelectField
            control={futureForm.control}
            name="locat"
            label="Place de cotation"
            placeholder="Choisir…"
            options={availableFutureLocats.map(l => ({ value: String(l.locat), label: l.nomLocat }))}
            rules={{ required: "Place obligatoire" }}
          />
        </div>
        <div style={{ width: 150 }}>
          <SelectField
            control={futureForm.control}
            name="mode"
            label="Mode"
            placeholder="Choisir…"
            options={MODE_OPTIONS}
            rules={{ required: "Mode obligatoire" }}
          />
        </div>
        <label style={{
          display: "flex", alignItems: "center", gap: 6, fontSize: 13, color: t.text2,
          paddingBottom: 10, whiteSpace: "nowrap", cursor: "pointer",
        }}>
          <input type="checkbox" {...futureForm.register("interdireCarnetOrdre")} />
          Carnet d'ordres interdit
          <span style={{ fontSize: 11, color: t.text3 }}>(Euronext différé)</span>
        </label>
        <button type="submit" disabled={addFutureMut.isPending} style={{
          border: "none", background: t.btnBg, color: "#fff", borderRadius: 8,
          padding: "10px 16px", fontSize: 13.5, fontWeight: 600, fontFamily: "inherit",
          cursor: addFutureMut.isPending ? "not-allowed" : "pointer",
          opacity: addFutureMut.isPending ? 0.7 : 1, whiteSpace: "nowrap",
        }}>
          {addFutureMut.isPending ? "Ajout..." : "Accorder une place"}
        </button>
      </form>
      {addFutureError && (
        <div style={{ padding: "10px 12px", background: t.lateBg, color: t.lateColor, borderRadius: 8, fontSize: 13, marginTop: 12 }}>
          {addFutureError}
        </div>
      )}

      <div style={{
        fontSize: 14, fontWeight: 700, color: t.text1, margin: "22px 0 10px",
        paddingTop: 16, borderTop: `1px solid ${t.divider}`,
      }}>Cash</div>
      {cashRights.length === 0 && (
        <div style={{ fontSize: 13, color: t.text2, padding: "0 0 10px" }}>Aucune place cash.</div>
      )}
      {cashRights.map(renderLocat)}

      <form onSubmit={onSubmitCash} style={{
        display: "flex", gap: 10, alignItems: "flex-end", paddingTop: 12, marginTop: 4,
      }}>
        <div style={{ flex: 1, maxWidth: 320 }}>
          <SelectField
            control={cashForm.control}
            name="locat"
            label="Place de cotation"
            placeholder="Choisir…"
            options={availableCashLocats.map(l => ({ value: String(l.locat), label: l.nomLocat }))}
            rules={{ required: "Place obligatoire" }}
          />
        </div>
        <button type="submit" disabled={addCashMut.isPending} style={{
          border: "none", background: t.btnBg, color: "#fff", borderRadius: 8,
          padding: "10px 16px", fontSize: 13.5, fontWeight: 600, fontFamily: "inherit",
          cursor: addCashMut.isPending ? "not-allowed" : "pointer",
          opacity: addCashMut.isPending ? 0.7 : 1, whiteSpace: "nowrap",
        }}>
          {addCashMut.isPending ? "Ajout..." : "Accorder une place cash"}
        </button>
      </form>
      {addCashError && (
        <div style={{ padding: "10px 12px", background: t.lateBg, color: t.lateColor, borderRadius: 8, fontSize: 13, marginTop: 12 }}>
          {addCashError}
        </div>
      )}
    </div>
  );
}
